import React from "react";

interface Step2Props {
    formData: any;
    handleArrayChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, index: number, arrayName: string) => void;
    handleAddEntry: (arrayName: string, newEntry: any) => void;
    handleRemoveEntry: (arrayName: string, index: number) => void;
}

const Step2: React.FC<Step2Props> = ({ formData, handleArrayChange, handleAddEntry, handleRemoveEntry }) => {
    return (
        <div className="bg-white shadow-sm rounded-lg p-6">
            <div className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900">Experience & Education</h2>
                <p className="mt-1 text-sm text-gray-600">Tell us about your professional experience and educational background.</p>
            </div>

            <div className="space-y-8">
                {/* Experience Section */}
                <div className="space-y-6">
                    <h3 className="text-lg font-medium text-gray-900">Professional Experience</h3>
                    {formData.experience.map((exp, index) => (
                        <div key={index} className="p-4 border border-gray-200 rounded-md space-y-4 hover:border-gray-300 transition-colors">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Company</label>
                                    <input
                                        type="text"
                                        name="company"
                                        placeholder="Company name"
                                        value={exp.company}
                                        onChange={(e) => handleArrayChange(e, index, "experience")}
                                        className="block w-full px-4 py-3 rounded-md border-gray-300 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-gray-400 transition-colors"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
                                    <input
                                        type="text"
                                        name="role"
                                        placeholder="Job title"
                                        value={exp.role}
                                        onChange={(e) => handleArrayChange(e, index, "experience")}
                                        className="block w-full px-4 py-3 rounded-md border-gray-300 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-gray-400 transition-colors"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                                    <input
                                        type="date"
                                        name="startDate"
                                        value={exp.startDate}
                                        onChange={(e) => handleArrayChange(e, index, "experience")}
                                        className="block w-full px-4 py-3 rounded-md border-gray-300 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-gray-400 transition-colors"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                                    <div className="space-y-2">
                                        <input
                                            type="date"
                                            name="endDate"
                                            value={exp.present ? "" : exp.endDate}
                                            onChange={(e) => handleArrayChange(e, index, "experience")}
                                            disabled={exp.present}
                                            className="block w-full px-4 py-3 rounded-md border-gray-300 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-gray-400 transition-colors disabled:bg-gray-50 disabled:text-gray-500"
                                        />
                                        <div className="flex items-center">
                                            <input
                                                type="checkbox"
                                                name="present"
                                                checked={exp.present}
                                                onChange={(e) => handleArrayChange(e, index, "experience")}
                                                className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                            />
                                            <label className="ml-2 text-sm text-gray-600">Currently working here</label>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                                <textarea
                                    name="description"
                                    placeholder="Describe your responsibilities and achievements..."
                                    value={exp.description}
                                    onChange={(e) => handleArrayChange(e, index, "experience")}
                                    rows={3}
                                    className="block w-full px-4 py-3 rounded-md border-gray-300 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-gray-400 transition-colors resize-none"
                                />
                            </div>

                            <button
                                type="button"
                                onClick={() => handleRemoveEntry("experience", index)}
                                className="mt-2 inline-flex items-center px-3 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                            >
                                Remove Entry
                            </button>
                        </div>
                    ))}
                    <button
                        type="button"
                        onClick={() => handleAddEntry("experience", { company: "", role: "", startDate: "", endDate: "", present: false, description: "" })}
                        className="inline-flex items-center px-4 py-2 border border-blue-300 text-sm font-medium rounded-md text-blue-700 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                        Add Experience
                    </button>
                </div>

                {/* Education Section */}
                <div className="space-y-6 pt-8 border-t border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900">Education</h3>
                    {formData.education.map((edu, index) => (
                        <div key={index} className="p-4 border border-gray-200 rounded-md space-y-4 hover:border-gray-300 transition-colors">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Institution</label>
                                    <input
                                        type="text"
                                        name="institution"
                                        placeholder="School or university name"
                                        value={edu.institution}
                                        onChange={(e) => handleArrayChange(e, index, "education")}
                                        className="block w-full px-4 py-3 rounded-md border-gray-300 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-gray-400 transition-colors"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Degree</label>
                                    <input
                                        type="text"
                                        name="degree"
                                        placeholder="e.g., Bachelor's, Master's"
                                        value={edu.degree}
                                        onChange={(e) => handleArrayChange(e, index, "education")}
                                        className="block w-full px-4 py-3 rounded-md border-gray-300 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-gray-400 transition-colors"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Field of Study</label>
                                    <input
                                        type="text"
                                        name="fieldOfStudy"
                                        placeholder="e.g., Computer Science"
                                        value={edu.fieldOfStudy}
                                        onChange={(e) => handleArrayChange(e, index, "education")}
                                        className="block w-full px-4 py-3 rounded-md border-gray-300 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-gray-400 transition-colors"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Duration</label>
                                    <input
                                        type="text"
                                        name="duration"
                                        placeholder="e.g., 2018 - 2022"
                                        value={edu.duration}
                                        onChange={(e) => handleArrayChange(e, index, "education")}
                                        className="block w-full px-4 py-3 rounded-md border-gray-300 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-gray-400 transition-colors"
                                    />
                                </div>
                            </div>

                            <button
                                type="button"
                                onClick={() => handleRemoveEntry("education", index)}
                                className="mt-2 inline-flex items-center px-3 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                            >
                                Remove Entry
                            </button>
                        </div>
                    ))}
                    <button
                        type="button"
                        onClick={() => handleAddEntry("education", { institution: "", degree: "", fieldOfStudy: "", duration: "" })}
                        className="inline-flex items-center px-4 py-2 border border-blue-300 text-sm font-medium rounded-md text-blue-700 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                        Add Education
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Step2;